import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SecureDocumentUploadApiService } from '@app/areas/documents/services/secure-document-upload-api.service';
import { AlertService } from '@services/helpers/alert/alert.service';
import { ButtonComponent } from '@shared/components/buttons/button/button.component';
import { CardComponent } from '@shared/components/cards/card/card.component';
import { DocumentsComponent } from '@app/areas/documents/components/documents/documents.component';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { FilePreviewService } from '@shared/services/file-preview/file-preview.service';
import { FilePreviewItem } from '@shared/models/file-preview-item';
import { DocumentDto, ListDirectoryResponse } from '@app/areas/documents/services/patient-documents-api.service';

const STORAGE_KEY = 'fitmed_secure_upload_token';

export type SecureUploadErrorType = 'missing' | 'invalid' | 'expired' | 'revoked' | 'generic' | null;

@Component({
  selector: 'app-secure-document-upload',
  standalone: true,
  imports: [ButtonComponent, CardComponent, DocumentsComponent, ModalComponent, IconComponent],
  templateUrl: './secure-document-upload.component.html',
})
export class SecureDocumentUploadComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(SecureDocumentUploadApiService);
  private readonly alertService = inject(AlertService);
  private readonly previewService = inject(FilePreviewService);

  token = '';
  rawTokenForResend = '';
  loadingFiles = false;
  loadingUpload = false;
  confirming = false;
  confirmModalOpen = false;
  locked = false;
  success = '';
  error = '';
  errorType: SecureUploadErrorType = null;
  errorDetails = '';
  linkExpiresAtMs: number | null = null;
  uploadsInFlight = 0;
  subdirs: Array<any> = [];
  docs: DocumentDto[] = [];

  get pendingDocs(): DocumentDto[] {
    return this.docs.filter(d => !d.metadata || d.metadata['ingested'] !== 'true');
  }

  get approvedDocs(): DocumentDto[] {
    return this.docs.filter(d => d.metadata && d.metadata['ingested'] === 'true');
  }

  requestingLink = false;
  resendSuccessMessage = '';
  cooldownRemainingSeconds = 0;
  private cooldownInterval: any = null;

  private readonly previewObjectUrls = new Map<string, string>();

  get expiryFormatted(): string {
    if (!this.linkExpiresAtMs) return '';
    const date = new Date(this.linkExpiresAtMs);
    return date.toLocaleString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  get resendCooldownFormatted(): string {
    const mins = Math.floor(this.cooldownRemainingSeconds / 60);
    const secs = this.cooldownRemainingSeconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  }

  get uploadsDisabled(): boolean {
    return this.locked || !this.token || !!this.errorType;
  }

  ngOnInit(): void {
    const tokenFromUrl = (this.route.snapshot.queryParamMap.get('token') ?? '').trim();
    const tokenFromSession = (sessionStorage.getItem(STORAGE_KEY) ?? '').trim();
    const token = tokenFromUrl || tokenFromSession;

    if (tokenFromUrl) {
      sessionStorage.setItem(STORAGE_KEY, tokenFromUrl);
    } else if (tokenFromSession) {
      sessionStorage.setItem(STORAGE_KEY, tokenFromSession);
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { token: tokenFromSession },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    this.token = token;
    this.rawTokenForResend = token;
    this.restoreCooldownTimer();

    if (!this.token) {
      this.locked = true;
      this.errorType = 'missing';
      this.errorDetails = 'No upload link token was provided.';
      return;
    }

    this.fetch();
  }

  ngOnDestroy(): void {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
    for (const url of this.previewObjectUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.previewObjectUrls.clear();
  }

  requestNewLink(): void {
    const rawToken = this.rawTokenForResend;
    if (!rawToken || this.requestingLink || this.cooldownRemainingSeconds > 0) {
      return;
    }

    this.requestingLink = true;
    this.resendSuccessMessage = '';

    this.api.requestNewUploadLink(rawToken)
      .pipe(finalize(() => (this.requestingLink = false)))
      .subscribe({
        next: (res) => {
          this.resendSuccessMessage = res?.message || 'If valid, a new upload link has been sent to your email.';
          const seconds = res?.cooldownSeconds ?? 300;
          this.startCooldownTimer(seconds);
        },
        error: (err) => {
          console.error('[SecureDocumentUpload] requestNewUploadLink failed', err);
          this.resendSuccessMessage = 'If valid, a new upload link has been sent to your email.';
          this.startCooldownTimer(300);
        },
      });
  }

  private startCooldownTimer(seconds: number): void {
    if (!this.rawTokenForResend) return;

    const cooldownUntil = Date.now() + seconds * 1000;
    sessionStorage.setItem(`fitmed_cooldown_${this.rawTokenForResend}`, String(cooldownUntil));
    this.cooldownRemainingSeconds = Math.max(0, seconds);

    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }

    this.cooldownInterval = setInterval(() => {
      const remaining = Math.ceil((cooldownUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        this.cooldownRemainingSeconds = 0;
        clearInterval(this.cooldownInterval);
        this.cooldownInterval = null;
        if (this.rawTokenForResend) {
          sessionStorage.removeItem(`fitmed_cooldown_${this.rawTokenForResend}`);
        }
      } else {
        this.cooldownRemainingSeconds = remaining;
      }
    }, 1000);
  }

  private restoreCooldownTimer(): void {
    if (!this.rawTokenForResend) return;
    const storedUntil = sessionStorage.getItem(`fitmed_cooldown_${this.rawTokenForResend}`);
    if (!storedUntil) return;

    const cooldownUntil = parseInt(storedUntil, 10);
    const remaining = Math.ceil((cooldownUntil - Date.now()) / 1000);
    if (remaining > 0) {
      this.startCooldownTimer(remaining);
    } else {
      sessionStorage.removeItem(`fitmed_cooldown_${this.rawTokenForResend}`);
    }
  }

  openConfirmModal(): void {
    if (!this.token || this.locked || this.confirming || !!this.errorType) {
      return;
    }
    this.confirmModalOpen = true;
  }

  closeConfirmModal(): void {
    if (this.confirming) {
      return;
    }
    this.confirmModalOpen = false;
  }

  onUpload(payload: { file: File; dirPath: string }): void {
    const token = this.token;
    if (!token || this.locked || !!this.errorType) {
      return;
    }

    this.uploadsInFlight += 1;
    this.loadingUpload = true;
    this.success = '';
    this.error = '';

    this.api.upload(token, payload.file)
      .pipe(finalize(() => {
        this.uploadsInFlight = Math.max(0, this.uploadsInFlight - 1);
        this.loadingUpload = this.uploadsInFlight > 0;
      }))
      .subscribe({
        next: () => {
          this.alertService.success('File uploaded successfully.');
          this.fetch();
        },
        error: (err) => {
          console.error('[SecureDocumentUpload] upload failed', err);
          this.handleApiError(err, 'Unable to upload the file. Please try again.');
        },
      });
  }

  onDownload(documentId: string): void {
    const token = this.token;
    if (!token || this.locked || !!this.errorType) {
      return;
    }

    const file = this.docs.find((doc) => doc.id === documentId);
    const name = file?.filename || 'download.bin';

    this.api.download(token, documentId).subscribe({
      next: (blob) => this.saveBlob(blob, name),
      error: (err) => {
        console.error('[SecureDocumentUpload] download failed', err);
        this.handleApiError(err, 'Unable to download the file. Please try again.');
      },
    });
  }

  async onPreview(documentId: string): Promise<void> {
    const token = this.token;
    if (!token || this.locked || !!this.errorType) {
      return;
    }

    const file = this.docs.find((doc) => doc.id === documentId);
    if (!file) return;

    const item: FilePreviewItem = {
      id: file.id,
      name: file.filename,
      mime: file.mimeType,
      source: 'document',
      previewLoader: async () => {
        try {
          const cached = this.previewObjectUrls.get(documentId);
          if (cached) {
            return cached;
          }

          const blob = await firstValueFrom(this.api.download(token, documentId));
          const previewUrl = URL.createObjectURL(blob);
          this.previewObjectUrls.set(documentId, previewUrl);
          return previewUrl;
        } catch (error) {
          console.error('[SecureDocumentUpload] preview failed', error);
          return '';
        }
      },
    };

    this.previewService.open([item]);
  }

  onRenameDocument(payload: { documentId: string; newFilename: string }): void {
    const token = this.token;
    if (!token || this.locked || !!this.errorType) {
      return;
    }

    const newFilename = (payload.newFilename || '').trim();
    if (!newFilename) return;

    this.api.rename(token, payload.documentId, newFilename).subscribe({
      next: () => this.fetch(),
      error: (err) => {
        console.error('[SecureDocumentUpload] rename failed', err);
        this.handleApiError(err, 'Unable to rename the file. Please try again.');
      },
    });
  }

  onDeleteDocument(documentId: string): void {
    const token = this.token;
    if (!token || this.locked || !!this.errorType) {
      return;
    }

    if (!window.confirm('Delete this file?')) {
      return;
    }

    this.api.delete(token, documentId).subscribe({
      next: () => {
        this.alertService.success('File deleted successfully.');
        this.fetch();
      },
      error: (err) => {
        console.error('[SecureDocumentUpload] delete failed', err);
        this.handleApiError(err, 'Unable to delete the file. Please try again.');
      },
    });
  }

  confirmUploads(): void {
    const token = this.token;
    if (!token || this.locked || this.confirming || !!this.errorType) {
      return;
    }

    this.confirming = true;
    this.success = '';
    this.error = '';

    this.api.confirm(token)
      .pipe(finalize(() => (this.confirming = false)))
      .subscribe({
        next: () => {
          sessionStorage.removeItem(STORAGE_KEY);
          this.token = '';
          this.locked = true;
          this.confirmModalOpen = false;
          this.errorType = 'revoked';
          this.errorDetails = 'This secure link has already been used and closed.';
          this.success = 'Uploads confirmed. Secure upload link is now closed.';
        },
        error: (err) => {
          console.error('[SecureDocumentUpload] confirm failed', err);
          this.handleApiError(err, 'Unable to confirm this upload link. Please try again.');
        },
      });
  }

  private fetch(): void {
    const token = this.token;
    if (!token || this.locked) {
      return;
    }

    this.loadingFiles = true;
    this.success = '';
    this.error = '';
    this.errorType = null;
    this.errorDetails = '';

    this.api.list(token)
      .pipe(finalize(() => (this.loadingFiles = false)))
      .subscribe({
        next: (res: ListDirectoryResponse) => {
          this.subdirs = res.directories ?? [];
          this.docs = res.documents ?? [];
          if (res.linkExpiresAt) {
            this.linkExpiresAtMs = res.linkExpiresAt < 1e11 ? res.linkExpiresAt * 1000 : res.linkExpiresAt;
            if (Date.now() > this.linkExpiresAtMs) {
              sessionStorage.removeItem(STORAGE_KEY);
              this.locked = true;
              this.token = '';
              this.errorType = 'expired';
              this.errorDetails = 'This secure upload link has expired.';
            }
          }
        },
        error: (err) => {
          console.error('[SecureDocumentUpload] list failed', err);
          this.handleApiError(err, 'Unable to load uploaded files. Please try again.');
        },
      });
  }

  private handleApiError(err: any, fallbackMessage: string): void {
    const rawMsg = `${err?.error?.error?.message ?? err?.error?.message ?? err?.message ?? err ?? ''}`.toLowerCase();

    if (rawMsg.includes('expired')) {
      sessionStorage.removeItem(STORAGE_KEY);
      this.locked = true;
      this.token = '';
      this.errorType = 'expired';
      this.errorDetails = 'This secure upload link has expired.';
      return;
    }

    if (rawMsg.includes('revoked') || rawMsg.includes('confirmed') || rawMsg.includes('closed')) {
      sessionStorage.removeItem(STORAGE_KEY);
      this.locked = true;
      this.token = '';
      this.errorType = 'revoked';
      this.errorDetails = 'This secure link has already been used and closed.';
      return;
    }

    if (rawMsg.includes('invalid') || rawMsg.includes('token') || rawMsg.includes('not found') || rawMsg.includes('internal error') || err?.status === 500 || err?.status === 400 || err?.status === 404) {
      sessionStorage.removeItem(STORAGE_KEY);
      this.locked = true;
      this.token = '';
      this.errorType = 'invalid';
      this.errorDetails = 'This upload link is invalid or incomplete.';
      return;
    }

    this.error = fallbackMessage;
  }

  private saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noreferrer';
    anchor.click();
    URL.revokeObjectURL(url);
  }
}
